/// <reference path="./env.d.ts" />
// ============================================
// @gs/config — Variables d'environnement GS (Zod)
// ============================================
import { z } from 'zod';
import { deploymentSlotSchema } from './copie';
/** Une variable posee vide (`CLE=` dans un .env) vaut une variable absente. */
function videCommeAbsent(v) {
    return typeof v === 'string' && v.trim() === '' ? undefined : v;
}
// ─── Schéma serveur ───────────────────────────────
/** SQL user/password requis sauf si Managed Identity (`SQL_USE_MANAGED_IDENTITY=true`). */
const serverEnvSchema = z
    .object({
    // Azure SQL (GS_Apps)
    AZURE_SQL_SERVER: z.string().min(1),
    AZURE_SQL_DATABASE: z.string().min(1).default('GS_Apps'),
    SQL_USE_MANAGED_IDENTITY: z.string().optional(),
    AZURE_SQL_USER: z.string().optional(),
    AZURE_SQL_PASSWORD: z.string().optional(),
    // Couture gardee (ADR-094 regle 2) : AZURE_SQL_ANL_* restent optionnelles.
    // Absentes, la couche analytics vise le meme serveur et GS_Apps.
    // Une seconde base ou un pool elastique ne s ouvre que par la question
    // fermee d ADR-094 regle 4 et son propre ADR.
    AZURE_SQL_ANL_SERVER: z.string().min(1).optional(),
    AZURE_SQL_ANL_DATABASE: z.string().min(1).optional().default('GS_Apps'),
    // Couture gardee (ADR-094 regle 2) : les trois pools visent GS_Apps
    // tant qu aucune variable ne les separe (repli vers AZURE_SQL_*).
    // Une seconde base ou un pool elastique ne s ouvre que par la question
    // fermee d ADR-094 regle 4 et son propre ADR.
    AZURE_SQL_OPS_SERVER: z.string().min(1).optional(),
    AZURE_SQL_OPS_DATABASE: z.string().min(1).optional(),
    AZURE_SQL_WAREHOUSE_SERVER: z.string().min(1).optional(),
    AZURE_SQL_WAREHOUSE_DATABASE: z.string().min(1).optional(),
    AZURE_SQL_AUDIT_SERVER: z.string().min(1).optional(),
    AZURE_SQL_AUDIT_DATABASE: z.string().min(1).optional(),
    // Base locale (lot feat-le-runtime-joint-une-base-locale) : un SQL Server en conteneur
    // sur le poste (certificat auto-signe, port possiblement autre que 1433), comme le
    // harnais RLS (ADR-058). Defauts = Azure SQL : 1433, chiffre, certificat verifie.
    // Forme fermee : une valeur mal formee se refuse en la nommant, jamais devinee (le port
    // est un entier decimal nu : ni hexadecimal, ni exposant, ni signe). En production
    // Azure, ou vers un serveur Azure SQL, une valeur qui assouplit le chiffrement ou la
    // verification du certificat arrete le demarrage (refuserSqlAssoupli).
    AZURE_SQL_PORT: z.preprocess(videCommeAbsent, z
        .string()
        .regex(/^\d{1,5}$/, 'entier decimal attendu')
        .transform(Number)
        .pipe(z.number().int().min(1).max(65535))
        .optional()),
    AZURE_SQL_ENCRYPT: z.preprocess(videCommeAbsent, z.enum(['true', 'false']).optional()),
    AZURE_SQL_TRUST_SERVER_CERTIFICATE: z.preprocess(videCommeAbsent, z.enum(['true', 'false']).optional()),
    // Azure AD workforce — optionnel. Requis par les apps web (MSAL/JWT workforce :
    // admin/inventaire/fit/hub/mcp), MAIS legitimement absent des backends sans auth
    // workforce — ex. api-pwa-auth (tenant External ID gsterrain, pas le workforce) et
    // api-pwa-sync. Ces backends consomment @gs/db (getServerConfig) sans AD ni storage ;
    // les forcer = dummies semantiquement faux (ADR-044, cleanliness). Les consommateurs
    // (getConfig / middlewares auth) les exigent au point d'usage, pas au boot.
    AZURE_AD_CLIENT_ID: z.string().guid().optional(),
    AZURE_AD_TENANT_ID: z.string().guid().optional(),
    AZURE_AD_REDIRECT_URI: z.string().url().optional(),
    // Azure Blob Storage — optionnel (requis seulement pour les apps avec upload).
    // Absent des backends sans storage (api-pwa-auth/api-pwa-sync) : pas de secret etale.
    // ADR-057 : Managed Identity -> seul le nom de compte suffit (URL blob derivee).
    AZURE_STORAGE_ACCOUNT: z.string().min(1).optional(),
    // DEPRECATED (ADR-057) : Shared Key. Toleree en transition (plus lue par @gs/storage).
    // A retirer du KV/App Settings apres le flip allowSharedKeyAccess=false (David, §STOP).
    AZURE_STORAGE_CONNECTION_STRING: z.string().min(1).optional(),
    AZURE_STORAGE_CONTAINER: z.string().min(1).default('gs-uploads'),
    // Observability — App Insights connection string (ADR-041 Phase 2).
    // Optionnel : vide en dev local => télémétrie serveur NO-OP silencieux.
    // Phase 2 = App Setting plain string ; Phase 3 = Key Vault reference (ADR-044).
    APPLICATIONINSIGHTS_CONNECTION_STRING: z.string().optional(),
    // SOFI — optionnel (requis uniquement pour l'ETL GS-Analytics)
    SOFI_SQL_SERVER: z.string().min(1).optional(),
    SOFI_SQL_DATABASE: z.string().min(1).optional().default('Signal_Location_XPA'),
    SOFI_SQL_USER: z.string().min(1).optional(),
    SOFI_SQL_PASSWORD: z.string().min(1).optional(),
    // Sélecteur de contexte A3 (ACCESS-CONTROL-STANDARD §5) — secret HMAC du cookie
    // signé qui porte le company_id de session choisi par un admin corporate.
    // OPTIONNEL (n'impacte pas la validation all-or-nothing des backends sans A3) :
    // absent => setContext répond 503 CONTEXT_UNAVAILABLE et le middleware ne
    // honore aucun cookie (fail-closed vers home). >= 16 chars quand présent.
    // App Setting Azure requis pour ACTIVER A3 en prod (prérequis déploiement).
    GS_SESSION_CONTEXT_SECRET: z.string().min(16).optional(),
    // Mailer ACS (@gs/mailer, FRAG R) — capability-aware : defauts absents = envoi
    // desactive (le service repond MAILER_NOT_CONFIGURED, jamais un crash). Deux
    // modes d'auth : GS_ACS_ENDPOINT (Managed Identity, mode cible) OU
    // GS_ACS_CONNECTION_STRING (fallback Key Vault). GS_MAIL_SENDER = adresse NUE
    // (DoNotReply@<domaine>.azurecomm.net). GS_MAIL_MAX_PER_MINUTE borne le throttle.
    GS_ACS_ENDPOINT: z.string().url().optional(),
    GS_ACS_CONNECTION_STRING: z.string().min(1).optional(),
    GS_MAIL_SENDER: z.string().min(1).optional(),
    GS_MAIL_MAX_PER_MINUTE: z.coerce.number().int().positive().optional(),
    // Throttle memoire des routes publiques de reponse au sondage gest
    // (PULSE-SUBMIT P-PS-3) — requetes/minute par IP. Absent => defaut 60.
    GS_PULSE_SUBMIT_MAX_PER_MINUTE: z.coerce.number().int().positive().optional(),
    // Secret partage du dispatcher pulse (item 9b) — garde l'endpoint machine
    // POST /api/internal/pulse-dispatch (cron GitHub Actions). Capability-aware
    // (P-DP-1) : absent => l'endpoint repond 503 DISPATCH_NOT_CONFIGURED (jamais
    // un crash) ; present => compare en TEMPS CONSTANT au header X-Dispatch-Secret.
    // Ne se log JAMAIS. >= 16 chars quand present. App Setting Azure requis pour
    // ACTIVER le dispatcher en prod (meme valeur que le GitHub Secret DISPATCH_SECRET).
    GS_DISPATCH_SECRET: z.string().min(16).optional(),
    // Secret partage de la route machine de ZoneSecur (POST /api/internal/taches/*,
    // appelee par le flux planifie). Absent => 503 ZSC_INTERNAL_NOT_CONFIGURED
    // (refus par defaut) ; present => compare en TEMPS CONSTANT a l en-tete
    // X-Zsc-Internal-Secret. Ne se journalise JAMAIS. >= 32 caracteres quand present.
    // Le coffre et le flux (zonesecur-planifie.yml) se posent au provisionnement.
    GS_ZONESECUR_INTERNAL_SECRET: z.string().min(32).optional(),
    // La cle des identifiants gouvernementaux de ZoneSecur (ADR-103 regles 4 et 5). Ni l un ni
    // l autre n est une cle : l adresse VERSIONNEE de la cle maitresse RSA au coffre, et les cles de
    // donnees ENVELOPPEES par elle, que seule l identite geree de ZoneSecur deballe. OPTIONNELS et
    // sans forme imposee ICI : le schema est tout-ou-rien, une valeur mal formee arreterait le
    // demarrage. Leur forme se juge dans `lireLaCleDuCoffreZonesecur` (plus bas) : absents = chiffrement
    // non configure (503) ; un seul des deux, ou une forme fausse = refus (503), jamais un arret.
    GS_ZONESECUR_CLE_MAITRESSE: z.preprocess(videCommeAbsent, z.string().optional()),
    GS_ZONESECUR_CLES_DE_DONNEES: z.preprocess(videCommeAbsent, z.string().optional()),
    // Les textos de ZoneSecur (lot feat-zonesecur-le-service-de-textos). Le numero sans frais d envoi
    // de `gs-acs`, en E.164 : un reglage du site, jamais au depot. Le drapeau dit si Microsoft a rendu
    // « Verified » : tant qu il ne vaut pas 'true', aucun texto ne part (« numero en verification »).
    // OPTIONNELS et sans forme imposee ICI (tout-ou-rien du schema, comme la cle du coffre) : leur
    // forme se juge dans l envoyeur ; absent ou mal forme = non configure (503), jamais un arret.
    // L adresse de la ressource est GS_ACS_ENDPOINT, deja au schema ; l identite, AZURE_CLIENT_ID.
    GS_ZONESECUR_SMS_NUMERO: z.preprocess(videCommeAbsent, z.string().optional()),
    GS_ZONESECUR_SMS_VERIFIE: z.preprocess(videCommeAbsent, z.string().optional()),
    // Alertes courriel creation d'affichage GEST (LOT A diffusion) — destinataires
    // fixes GS + URL publique de l'app (lien profond /affichages/:id). Defauts =
    // production GS. Preuve URL : .github/workflows/master_gest.yml
    // (VITE_API_URL / VITE_AZURE_AD_REDIRECT_URI = https://gest.groupesignalisation.ca).
    GS_GEST_POSTING_NOTIFY_RECRUITMENT: z.string().email().default('recrutement@groupesignalisation.ca'),
    GS_GEST_POSTING_NOTIFY_ADMIN: z.string().email().default('administration@groupesignalisation.ca'),
    GS_GEST_POSTING_NOTIFY_IT: z.string().email().default('ti@groupesignalisation.ca'),
    GS_GEST_APP_URL: z.string().url().default('https://gest.groupesignalisation.ca'),
    // Identite d application et emplacement Azure — lus par la page « Sante du parc » d admin
    // (feat-l-ecran-sante-du-parc, 2026-09-25). OPTIONNELS, et c est la regle : le schema est
    // tout-ou-rien, une variable requise ici arreterait le demarrage des 7 apps. Leur absence
    // rend la telemetrie « illisible (configuration) » sur la page, jamais un arret.
    //   AZURE_CLIENT_ID        identite d application (ADR-079), posee par tools/infrastructure/apps/<app>.bicep
    //   WEBSITE_OWNER_NAME     pose par App Service : `<abonnement>+<groupe>-<region>webspace`
    //   WEBSITE_RESOURCE_GROUP pose par App Service : le groupe de ressources de l app
    AZURE_CLIENT_ID: z.string().min(1).optional(),
    WEBSITE_OWNER_NAME: z.string().min(1).optional(),
    WEBSITE_RESOURCE_GROUP: z.string().min(1).optional(),
    // L application GitHub de la console, en LECTURE seule (lot feat-la-console-montre-l-approbation-du-soir,
    // 2026-10-29) : le passage du soir qui attend l approbation de David. AUCUN SECRET ici : l identifiant
    // client n en est pas un, et la cle privee vit au coffre en signature seule — ce reglage porte son
    // ADRESSE versionnee, jamais la cle. OPTIONNELS (schema tout-ou-rien) : absents ou mal formes = la page
    // dit « lecture de GitHub pas encore configuree » ; la forme se juge dans le client de la console.
    GS_CONSOLE_GITHUB_APP_CLIENT_ID: z.preprocess(videCommeAbsent, z.string().optional()),
    GS_CONSOLE_GITHUB_APP_CLE: z.preprocess(videCommeAbsent, z.string().optional()),
    /** Mode declenchement ETL admin : inprocess (defaut) ou queue (ADR-061 D2, STOP-4). */
    ETL_TRIGGER_MODE: z.enum(['queue', 'inprocess']).default('inprocess'),
    /** Compte Storage de la queue etl-sync-trigger (requis si ETL_TRIGGER_MODE=queue). */
    ETL_QUEUE_ACCOUNT: z.string().min(1).optional(),
    ETL_QUEUE_NAME: z.string().min(1).optional().default('etl-sync-trigger'),
    /**
     * Les files des applications qui administrent, lues par le worker ETL avec celle de gs-admin
     * (DECISION-2026-10-16-UNE-FILE-PAR-APPLICATION) : `fournisseur=file` separes par des virgules,
     * ex. `gest=etl-saisie-gest,ug=etl-saisie-ug`. Chaque file ne porte que les demandes de son
     * fournisseur. Forme jugee par @gs/etl `lireLaConfigurationDesFiles`, pas ici : le schema est
     * tout-ou-rien, une valeur fausse arreterait toutes les apps. Absente ou vide : aucune file
     * d application — d ou l absence de `.min(1)` : un parametre bicep vide ne fait tomber personne.
     */
    ETL_QUEUES_SAISIE: z.string().optional(),
    /** Tables business_state remises à NULL au true-up hebdomadaire (CSV schema.table). */
    ETL_TRUEUP_TABLES: z.string().min(1).optional().default('stg_sofi.Phases'),
    /**
     * Bronze de l usine Goliax (stg_goliax.*, lot feat-le-bronze-de-l-usine) : 'true' fait
     * appeler runGoliaxETL par le worker, apres le pipeline SOFI. Defaut 'false' : rien ne
     * change tant que le job gs-etl ne porte pas la variable (infra/azure/etl-worker/main.bicep,
     * param etlGoliaxEnabled, qui ne la pose qu a true).
     */
    ETL_GOLIAX_ENABLED: z.enum(['true', 'false']).default('false'),
    /**
     * La copie du journal des acces vers Log Analytics (ADR-087 regle 7) : le point d ingestion,
     * l identifiant immuable de la regle de collecte et l identifiant de l espace, sortis du
     * deploiement de infra/azure/socle/le-journal-des-acces.bicep. OPTIONNELS et sans forme
     * imposee ICI, et c est voulu : le schema est tout-ou-rien, une valeur mal formee arreterait les
     * 7 apps. Leur forme se juge dans `destinationDuJournal` (@gs/etl) : aucune = etape sautee,
     * une partie ou une valeur fausse = etape rouge. Poses par infra/azure/etl-worker/main.bicep
     * (param journalDesAccesEnabled, qui ne les pose qu a true).
     */
    ETL_JOURNAL_ACCES_INGESTION_ENDPOINT: z.string().optional(),
    ETL_JOURNAL_ACCES_DCR_IMMUTABLE_ID: z.string().optional(),
    ETL_JOURNAL_ACCES_WORKSPACE_ID: z.string().optional(),
    /**
     * La passe du terrain (ADR-102 regle 3, lot feat-le-terrain-est-invite-depuis-sofi) : l interrupteur,
     * l URL de retour de l invitation (`inviteRedirectUrl`) et le plafond de blocages par nuit.
     * OPTIONNELS et sans forme imposee ICI, comme ETL_JOURNAL_ACCES_* : leur forme se juge dans
     * `etatDeLaPasse` (@gs/etl). Aucune, ou ETL_TERRAIN_ACTIVE a false = passe eteinte ; partielle ou
     * fausse = etape rouge. L allumage est un geste de David, apres le droit Graph de `gs-etl`, le
     * droit en base et l EFVP qui couvre la copie de l adresse vers Microsoft.
     */
    ETL_TERRAIN_ACTIVE: z.string().optional(),
    ETL_TERRAIN_URL_DE_RETOUR: z.string().optional(),
    ETL_TERRAIN_PLAFOND_BLOCAGES: z.string().optional(),
    NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),
})
    .superRefine((data, ctx) => {
    const mi = data.SQL_USE_MANAGED_IDENTITY === 'true';
    if (mi)
        return;
    if (!data.AZURE_SQL_USER?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['AZURE_SQL_USER'],
        });
    }
    if (!data.AZURE_SQL_PASSWORD?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Required',
            path: ['AZURE_SQL_PASSWORD'],
        });
    }
});
// ─── Schéma ETL-only (SOFI requis) ───────────────────────────
// Utilisé par l'ETL GS-Analytics — valide que SOFI est configuré
const sofiEnvSchema = z.object({
    SOFI_SQL_SERVER: z.string().min(1, 'SOFI_SQL_SERVER manquant — vérifier .env'),
    SOFI_SQL_DATABASE: z.string().min(1).default('Signal_Location_XPA'),
    SOFI_SQL_USER: z.string().min(1, 'SOFI_SQL_USER manquant — vérifier .env'),
    SOFI_SQL_PASSWORD: z.string().min(1, 'SOFI_SQL_PASSWORD manquant — vérifier .env'),
});
// zod v4 a scinde l ancien `.uuid()` de v3 en deux : `.uuid()` applique
// desormais RFC 9562 a la lettre (quartets de version ET de variante), tandis
// que `.guid()` garde le controle de v3 — huit-quatre-quatre-quatre-douze
// hexa. Nos identifiants sont des GUID Microsoft (UNIQUEIDENTIFIER SQL, objets
// Azure AD), pas des UUID RFC : les deux seules compagnies en production,
// 00000000-0000-0000-0000-000000000001 (GS) et ...00FF (UG corporate), sont
// REFUSEES par le `.uuid()` de v4. `.guid()` est donc l equivalent exact du
// comportement v3, et le validateur juste pour ce que la colonne contient.
const uuidOrEmpty = z.union([z.string().guid(), z.literal('')]);
const optionalUrl = z.preprocess((v) => (v === '' || v === undefined ? undefined : v), z.string().url().optional());
// ─── Schéma client (Vite) ────────────────────────
/** Schéma client Vite — exporté pour `@gs/config/browser` uniquement */
export const clientEnvSchema = z.object({
    VITE_AZURE_AD_CLIENT_ID: uuidOrEmpty.default(''),
    VITE_AZURE_AD_TENANT_ID: uuidOrEmpty.default(''),
    VITE_AZURE_AD_REDIRECT_URI: optionalUrl,
    VITE_API_URL: z.preprocess((v) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : 'http://localhost:3001'), z.string().url()),
});
// Export du schéma RÉEL (pas une copie) — consommé par le test de contrat IaC
// packages/standards/src/iac-contract.test.ts (ADR-065 : le module bicep gs-app.bicep
// doit poser des App Settings que CE schéma accepte — enum NODE_ENV, AZURE_SQL_*).
export { serverEnvSchema };
let _serverConfig = null;
function formatZodIssues(err) {
    return err.issues.map((e) => `${e.path.join('.') || '(root)'}: ${e.message}`).join('; ');
}
/**
 * Valide `process.env` pour les backends Node.
 * À appeler au démarrage du serveur Express.
 */
export function getServerConfig() {
    if (_serverConfig)
        return _serverConfig;
    const result = serverEnvSchema.safeParse(process.env);
    if (!result.success) {
        throw new Error(`[gs/config] Variables d'environnement invalides : ${formatZodIssues(result.error)}`);
    }
    const d = result.data;
    refuserSqlAssoupli(d);
    _serverConfig = {
        ...d,
        AZURE_SQL_USER: d.AZURE_SQL_USER ?? '',
        AZURE_SQL_PASSWORD: d.AZURE_SQL_PASSWORD ?? '',
    };
    return _serverConfig;
}
// ─── Schéma du proxy gs-admin (outils MCP gs_sofi_audit / gs_etl_conformity) ───
// Accesseur ETROIT, sur le modele de `getSofiConfig()` : ces deux outils tournent aussi sur
// les postes en mode stdio, dont `config.env` ne porte pas AZURE_SQL_* ; `getServerConfig()`
// y leverait avant meme de lire l adresse. Aucune valeur de repli : une cle absente se dit.
const adminProxyEnvSchema = z.object({
    GS_ADMIN_API_BASE: z.string().url(),
    MCP_SECRET_TOKEN: z.string().min(1),
});
/**
 * Retourne l adresse de gs-admin et le jeton interne, sans repli en dur.
 * Lance une erreur qui NOMME chaque cle manquante ou invalide (jamais sa valeur).
 */
export function getAdminProxyConfig() {
    const result = adminProxyEnvSchema.safeParse(process.env);
    if (!result.success) {
        const cles = [...new Set(result.error.issues.map((i) => String(i.path[0])))];
        throw new Error(`${cles.map((c) => `${c} manquant ou invalide`).join(', ')} sur gs-mcp`);
    }
    return {
        adminApiBase: result.data.GS_ADMIN_API_BASE.replace(/\/$/, ''),
        secretToken: result.data.MCP_SECRET_TOKEN,
    };
}
// ─── Les portes machine (ADR-106 regles 2 et 4.7) ───────────────────────────
// Accesseur ETROIT, sur le modele de `getAdminProxyConfig()`. Chaque porte machine d une application
// lit DEUX reglages de son modele d infrastructure (`tools/infrastructure/apps/<app>.bicep`), nommes
// d apres la porte : le role d application qu elle exige et la liste des appelants declares (les
// `oid` des identites qui peuvent l ouvrir). Plus l audience et le locataire de « GS Apps », deja
// poses sur chaque application. Un reglage absent ou mal forme FERME la porte (refus stable), jamais
// un arret au demarrage : le schema serveur est tout-ou-rien, ces reglages n y sont donc pas.
/** Nom d une porte : minuscules, chiffres et tirets (`fabrique-des-cles`, `pulse-dispatch`). */
export const FORME_DU_NOM_DE_PORTE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** Role d une porte machine : `<Application>.<Porte>.<Action>` (ADR-106 regle 2). */
export const FORME_DU_ROLE_DE_PORTE = /^[A-Za-z][A-Za-z0-9]*(?:\.[A-Za-z][A-Za-z0-9]*){2,}$/;
/** Au plus vingt appelants par porte : une liste plus longue n est plus une liste d identites dediees. */
export const APPELANTS_PAR_PORTE_AU_PLUS = 20;
const FORME_DE_GUID_DE_PORTE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Les deux reglages d une porte, nommes d apres elle : `GS_PORTE_MACHINE_<NOM>_ROLE` et `_APPELANTS`. */
export function reglagesDeLaPorteMachine(porte) {
    const base = `GS_PORTE_MACHINE_${porte.toUpperCase().replace(/-/g, '_')}`;
    return { role: `${base}_ROLE`, appelants: `${base}_APPELANTS` };
}
/**
 * La configuration d une porte machine, lue dans l environnement. Fermee = la liste des reglages
 * manquants ou mal formes, par leur NOM (jamais une valeur). Les appelants sont rendus en minuscules,
 * sans doublon.
 */
export function lireLaPorteMachine(porte, env = process.env) {
    if (!FORME_DU_NOM_DE_PORTE.test(porte))
        return { ouverte: false, manques: ['nom de porte'] };
    const noms = reglagesDeLaPorteMachine(porte);
    const manques = [];
    const role = (env[noms.role] ?? '').trim();
    if (!FORME_DU_ROLE_DE_PORTE.test(role))
        manques.push(noms.role);
    const brut = (env[noms.appelants] ?? '').split(',').map((a) => a.trim().toLowerCase()).filter((a) => a !== '');
    const appelants = [...new Set(brut)];
    if (appelants.length === 0 || appelants.length > APPELANTS_PAR_PORTE_AU_PLUS || !appelants.every((a) => FORME_DE_GUID_DE_PORTE.test(a))) {
        manques.push(noms.appelants);
    }
    const audience = (env.AZURE_AD_CLIENT_ID ?? '').trim().toLowerCase();
    if (!FORME_DE_GUID_DE_PORTE.test(audience))
        manques.push('AZURE_AD_CLIENT_ID');
    const locataire = (env.AZURE_AD_TENANT_ID ?? '').trim().toLowerCase();
    if (!FORME_DE_GUID_DE_PORTE.test(locataire))
        manques.push('AZURE_AD_TENANT_ID');
    if (manques.length > 0)
        return { ouverte: false, manques };
    return { ouverte: true, role, appelants, audience, locataire };
}
/**
 * Retourne la configuration SOFI validée.
 * À appeler uniquement depuis l'ETL (@gs/etl, apps/etl-worker).
 * Lance une erreur explicite si SOFI_SQL_* ne sont pas dans le .env.
 */
export function getSofiConfig() {
    const result = sofiEnvSchema.safeParse(process.env);
    if (!result.success) {
        throw new Error(`[gs/config] SOFI non configuré : ${formatZodIssues(result.error)}\n` +
            'Récupérer SOFI_SQL_PASSWORD dans Azure App Service → gs-admin → Configuration.');
    }
    return {
        server: result.data.SOFI_SQL_SERVER,
        database: result.data.SOFI_SQL_DATABASE,
        user: result.data.SOFI_SQL_USER,
        password: result.data.SOFI_SQL_PASSWORD,
        port: 1433,
        options: { encrypt: false, trustServerCertificate: true },
    };
}
// ─── Tuning ETL (MASTER-ETL-PAGINATION-KEYSET 2026-07-12) ────────────────────
// Schéma étroit avec défauts (pattern getSofiConfig) : ne bloque JAMAIS un
// démarrage — une valeur absente prend le défaut, une valeur invalide est
// signalée en warn et retombe sur le défaut.
const ETL_RUNNER_TIMEOUT_MIN_DEFAULT = 18;
const SOFI_REQUEST_TIMEOUT_MS_DEFAULT = 300_000;
const ETL_TRANSFORM_REQUEST_TIMEOUT_MS_DEFAULT = 300_000;
const etlTuningSchema = z.object({
    /** Watchdog du pipeline analytics complet (runSofiETL), en minutes. */
    ETL_RUNNER_TIMEOUT_MIN: z.coerce.number().int().positive().default(ETL_RUNNER_TIMEOUT_MIN_DEFAULT),
    /** requestTimeout du driver mssql vers SOFI (GS-SQL01), en millisecondes. */
    SOFI_REQUEST_TIMEOUT_MS: z.coerce.number().int().positive().default(SOFI_REQUEST_TIMEOUT_MS_DEFAULT),
    /**
     * requestTimeout du driver mssql sur le pool `warehouse` (Azure SQL GS_Apps),
     * en millisecondes — celui qu'empruntent les transforms medallion.
     *
     * Le pool `ops` reste a 120 000 ms : c'est la protection des routes API, on ne
     * l'echange pas contre le confort de l'ETL. Voir @gs/db connection.ts.
     */
    ETL_TRANSFORM_REQUEST_TIMEOUT_MS: z.coerce
        .number()
        .int()
        .positive()
        .default(ETL_TRANSFORM_REQUEST_TIMEOUT_MS_DEFAULT),
});
/**
 * Tuning des timeouts ETL — consommé par @gs/etl (runners/sofi.ts, sofiConnection.ts).
 * Le watchdog 18 min en dur avait tué le 1er full reload BonLocation post-reset
 * watermark ([BUG-ETL-BONLOCATION-OFFSET-TIMEOUT]) : rendre le plafond pilotable
 * par env sans redéploiement. Défauts INCHANGÉS (18 min / 300 000 ms).
 */
export function getEtlTuningConfig() {
    const result = etlTuningSchema.safeParse(process.env);
    if (!result.success) {
        console.warn(`[gs/config] Tuning ETL invalide (${formatZodIssues(result.error)}) — ` +
            `défauts appliqués : ${ETL_RUNNER_TIMEOUT_MIN_DEFAULT} min / ${SOFI_REQUEST_TIMEOUT_MS_DEFAULT} ms`);
        return {
            runnerTimeoutMin: ETL_RUNNER_TIMEOUT_MIN_DEFAULT,
            sofiRequestTimeoutMs: SOFI_REQUEST_TIMEOUT_MS_DEFAULT,
            transformRequestTimeoutMs: ETL_TRANSFORM_REQUEST_TIMEOUT_MS_DEFAULT,
        };
    }
    return {
        runnerTimeoutMin: result.data.ETL_RUNNER_TIMEOUT_MIN,
        sofiRequestTimeoutMs: result.data.SOFI_REQUEST_TIMEOUT_MS,
        transformRequestTimeoutMs: result.data.ETL_TRANSFORM_REQUEST_TIMEOUT_MS,
    };
}
// Le schema vit avec le garde de la copie (./copie), qui lit le MEME reglage.
/**
 * L emplacement de cette instance, lu du reglage COLLANT `GS_EMPLACEMENT` que
 * `tools/infrastructure/modules/gs-app.bicep` ne pose que sur l emplacement `preprod`.
 * Lu au SERVEUR, jamais a la construction : le meme paquet sert la copie puis la
 * production, une variable Vite serait identique des deux cotes.
 *
 * Schema etroit (pattern getEtlTuningConfig) : ne bloque JAMAIS un demarrage. Absent
 * = `production` ; une valeur inconnue est signalee et retombe aussi sur `production`
 * — le bandeau de preproduction ne s affiche que sur une declaration explicite.
 */
export function getDeploymentSlot() {
    const result = deploymentSlotSchema.safeParse(process.env);
    if (!result.success) {
        console.warn(`[gs/config] GS_EMPLACEMENT invalide (${formatZodIssues(result.error)}) — production retenue`);
        return 'production';
    }
    return result.data.GS_EMPLACEMENT;
}
// ─── La copie coupe ses sorties : le garde vit dans `./copie` (sous-chemin `@gs/config/copie`) ───
// Sous-chemin a part : les 54 temoins qui remplacent `@gs/config` par un faux ne le portent pas, et
// un point de passage d une sortie le lit sans tirer tout le schema.
export { getCopie, getCourrielDeLaCopie, getEtatDuCourrielDeLaCopie, sortiesDeLaCopie, BOITE_DE_TEST_PAR_DEFAUT, DOMAINE_DES_BOITES_DE_LA_COPIE, } from './copie';
// ─── ZoneSecur : la cle du coffre (ADR-103 regles 3 a 5, lot feat-zonesecur-la-cle-du-coffre) ───
/** La forme d une version de cle de donnees (ADR-103 regle 3) : un identifiant, jamais une cle. */
export const FORME_DE_VERSION_DE_CLE = /^[a-z][a-z0-9-]{0,40}-v[0-9]{1,6}$/;
/** L adresse d une cle au coffre, VERSIONNEE : `https://<coffre>.vault.azure.net/keys/<nom>/<version>`. */
const FORME_D_ADRESSE_DE_CLE = /^(https:\/\/[a-z0-9-]{3,24}\.vault\.azure\.net\/keys\/[A-Za-z0-9-]{1,127})\/([0-9a-f]{32})$/;
/** La version d une cle au coffre : 32 caracteres hexadecimaux. */
const FORME_DE_VERSION_DE_CLE_MAITRESSE = /^[0-9a-f]{32}$/;
/**
 * Une cle de donnees enveloppee par RSA-OAEP-256, en base64url sans remplissage : 342, 512 ou
 * 683 caracteres, soit 256, 384 ou 512 octets (cle maitresse RSA de 2048, 3072 ou 4096 bits).
 */
const LONGUEURS_D_ENVELOPPE = new Set([342, 512, 683]);
/** Borne du nombre de versions gardees en lecture : autant d appels au coffre au demarrage. */
const VERSIONS_AU_PLUS = 20;
const versionDeCleSchema = z.string().regex(FORME_DE_VERSION_DE_CLE);
const enveloppeSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]+$/)
    .refine((v) => LONGUEURS_D_ENVELOPPE.has(v.length));
/**
 * Une entree : la cle enveloppee seule (par la version de cle maitresse de `GS_ZONESECUR_CLE_MAITRESSE`),
 * ou la cle enveloppee et la version de la MEME cle maitresse qui l a enveloppee. La seconde forme
 * garde lisible une cle de donnees enveloppee avant une rotation de la cle maitresse : seule
 * l identite de ZoneSecur deballe (ADR-103 regle 6), personne d autre ne peut la reenvelopper.
 */
const entreeDeCleSchema = z.union([
    enveloppeSchema,
    z.object({ enveloppee: enveloppeSchema, cleMaitresse: z.string().regex(FORME_DE_VERSION_DE_CLE_MAITRESSE) }).strict(),
]);
/**
 * `GS_ZONESECUR_CLES_DE_DONNEES`, en JSON : la version d ecriture, et chaque version lisible avec
 * sa cle enveloppee. Exemple de forme : `{"courante":"zsc-dk-v2","cles":{"zsc-dk-v1":{"enveloppee":
 * "<base64url>","cleMaitresse":"<32 hexadecimaux>"},"zsc-dk-v2":"<base64url>"}}`. Une rotation
 * ajoute une version et deplace `courante` (ADR-103 regle 5).
 */
export const clesDeDonneesZonesecurSchema = z
    .object({
    courante: versionDeCleSchema,
    cles: z.record(versionDeCleSchema, entreeDeCleSchema),
})
    .strict()
    .refine((v) => Object.prototype.hasOwnProperty.call(v.cles, v.courante))
    .refine((v) => Object.keys(v.cles).length <= VERSIONS_AU_PLUS);
/**
 * Juge la forme des deux reglages de la cle du coffre de ZoneSecur, sans jamais lever : absents
 * tous deux = `absente` ; un seul, une adresse non versionnee ou hors `*.vault.azure.net`, un JSON
 * illisible ou hors forme = `invalide`, qui ne nomme que les reglages, jamais leur contenu (le
 * message d un `JSON.parse` recopie le debut de la valeur : il n est pas garde).
 */
export function lireLaCleDuCoffreZonesecur(reglages) {
    const adresse = reglages.GS_ZONESECUR_CLE_MAITRESSE;
    const brut = reglages.GS_ZONESECUR_CLES_DE_DONNEES;
    if (adresse === undefined && brut === undefined)
        return { etat: 'absente' };
    const fautifs = [];
    const morceaux = typeof adresse === 'string' ? FORME_D_ADRESSE_DE_CLE.exec(adresse) : null;
    if (!morceaux)
        fautifs.push('GS_ZONESECUR_CLE_MAITRESSE');
    let cles = null;
    if (typeof brut === 'string') {
        let json;
        try {
            json = JSON.parse(brut);
        }
        catch {
            json = undefined;
        }
        const lu = clesDeDonneesZonesecurSchema.safeParse(json);
        if (lu.success)
            cles = lu.data;
    }
    if (!cles)
        fautifs.push('GS_ZONESECUR_CLES_DE_DONNEES');
    if (fautifs.length > 0 || !cles || !morceaux)
        return { etat: 'invalide', reglages: fautifs };
    const [, cle, versionParDefaut] = morceaux;
    return {
        etat: 'configuree',
        courante: cles.courante,
        enveloppees: Object.entries(cles.cles).map(([version, entree]) => typeof entree === 'string'
            ? { version, enveloppee: entree, adresse: `${cle}/${versionParDefaut}` }
            : { version, enveloppee: entree.enveloppee, adresse: `${cle}/${entree.cleMaitresse}` }),
    };
}
/**
 * Configuration agrégée pour Node (Azure SQL, storage, etc.).
 * Sans `import.meta` — sûr pour bundle CJS (esbuild serveur).
 * Pour le navigateur : `import { getConfig } from '@gs/config/browser'`.
 */
/**
 * Config effective pour la couche analytics Azure SQL.
 * Couture gardee (ADR-094 regle 2) : identique a GS_Apps tant que les
 * variables AZURE_SQL_ANL_* ne sont pas definies. Une seconde base ou un
 * pool elastique ne s ouvre que par la question fermee d ADR-094 regle 4
 * et son propre ADR.
 */
export function getAnalyticsDbConfig() {
    const s = getServerConfig();
    return {
        server: s.AZURE_SQL_ANL_SERVER ?? s.AZURE_SQL_SERVER,
        database: s.AZURE_SQL_ANL_DATABASE ?? s.AZURE_SQL_DATABASE,
        user: s.AZURE_SQL_USER ?? '',
        password: s.AZURE_SQL_PASSWORD ?? '',
        ...transportSql(s),
    };
}
/** Un des serveurs configures est un Azure SQL (`*.database.windows.net`). */
function cibleAzureSql(s) {
    return [
        s.AZURE_SQL_SERVER,
        s.AZURE_SQL_OPS_SERVER,
        s.AZURE_SQL_WAREHOUSE_SERVER,
        s.AZURE_SQL_AUDIT_SERVER,
        s.AZURE_SQL_ANL_SERVER,
    ].some((serveur) => serveur?.trim().toLowerCase().endsWith('.database.windows.net'));
}
/**
 * Pourquoi le transport SQL ne peut pas etre assoupli ici, ou null : en production Azure
 * (`isProductionAzure()`, ADR-014), ou des qu un serveur configure est un Azure SQL — un
 * poste qui vise la preproduction ne desactive pas plus la verification du certificat.
 * La seconde condition couvre aussi un travail Container Apps dont NODE_ENV deriverait :
 * la plateforme n y pose pas WEBSITE_SITE_NAME, mais sa cible reste un Azure SQL.
 */
function transportVerrouille(s) {
    if (isProductionAzure())
        return 'en production Azure';
    if (cibleAzureSql(s))
        return 'vers un serveur Azure SQL (*.database.windows.net)';
    return null;
}
/**
 * La garde de la base locale, fail-closed : en production Azure ou vers un Azure SQL, la
 * connexion SQL est TOUJOURS chiffree et son certificat TOUJOURS verifie. Une variable qui
 * tente de l assouplir arrete le demarrage avec un message qui la nomme — elle n est
 * jamais ignoree en silence. Le port, lui, reste libre : il ne touche ni au chiffrement
 * ni a la verification.
 */
function refuserSqlAssoupli(s) {
    const motif = transportVerrouille(s);
    if (!motif)
        return;
    const refus = [];
    if (s.AZURE_SQL_ENCRYPT === 'false')
        refus.push('AZURE_SQL_ENCRYPT=false');
    if (s.AZURE_SQL_TRUST_SERVER_CERTIFICATE === 'true')
        refus.push('AZURE_SQL_TRUST_SERVER_CERTIFICATE=true');
    if (refus.length === 0)
        return;
    throw new Error(`[gs/config] ${refus.join(' et ')} refuse ${motif} : la connexion SQL y est toujours ` +
        'chiffree et son certificat toujours verifie. Ces variables ne servent qu a une base ' +
        'locale en conteneur sur le poste ; retirer le reglage.');
}
/**
 * Port et options de transport des configurations Azure SQL, lus de
 * AZURE_SQL_PORT / AZURE_SQL_ENCRYPT / AZURE_SQL_TRUST_SERVER_CERTIFICATE.
 * Quand le transport est verrouille, chiffrement et verification sont forces ici aussi :
 * la garde de getServerConfig() a deja refuse toute valeur contraire, ceci tient si la
 * config en cache a ete lue avant que l environnement change.
 */
function transportSql(s) {
    const verrouille = transportVerrouille(s) !== null;
    return {
        port: s.AZURE_SQL_PORT ?? 1433,
        options: {
            encrypt: verrouille || s.AZURE_SQL_ENCRYPT !== 'false',
            trustServerCertificate: !verrouille && s.AZURE_SQL_TRUST_SERVER_CERTIFICATE === 'true',
        },
    };
}
function buildPoolConfig(serverOverride, databaseOverride) {
    const s = getServerConfig();
    return {
        server: serverOverride ?? s.AZURE_SQL_SERVER,
        database: databaseOverride ?? s.AZURE_SQL_DATABASE,
        user: s.AZURE_SQL_USER ?? '',
        password: s.AZURE_SQL_PASSWORD ?? '',
        ...transportSql(s),
    };
}
export function getOpsSqlConfig() {
    const s = getServerConfig();
    return buildPoolConfig(s.AZURE_SQL_OPS_SERVER, s.AZURE_SQL_OPS_DATABASE);
}
export function getWarehouseSqlConfig() {
    const s = getServerConfig();
    return buildPoolConfig(s.AZURE_SQL_WAREHOUSE_SERVER, s.AZURE_SQL_WAREHOUSE_DATABASE);
}
export function getAuditSqlConfig() {
    const s = getServerConfig();
    return buildPoolConfig(s.AZURE_SQL_AUDIT_SERVER, s.AZURE_SQL_AUDIT_DATABASE);
}
export function getPoolSqlConfig(name) {
    switch (name) {
        case 'ops': return getOpsSqlConfig();
        case 'warehouse': return getWarehouseSqlConfig();
        case 'audit': return getAuditSqlConfig();
    }
}
/**
 * Détection robuste de la prod Azure (cf ADR-014).
 *
 * `NODE_ENV` seul est insuffisant : sur Azure App Service Linux, la variable
 * peut être absente ou valoir `development` malgré un déploiement prod, ce qui
 * a déjà causé un bug 2026-03 (`isDev = NODE_ENV === 'development'` exposait
 * les stack traces en clair). On combine donc avec `WEBSITE_SITE_NAME` qui est
 * toujours défini par App Service.
 *
 * Utilisation par défaut côté serveur :
 * - `errorHandler` (expose stack en dev only)
 * - features de debug (CORS open, healthz verbose, etc.)
 */
export function isProductionAzure() {
    return process.env.NODE_ENV === 'production' || !!process.env.WEBSITE_SITE_NAME;
}
/** Inverse de `isProductionAzure()` — sécurité par défaut : tout sauf prod = dev. */
export function isDevelopment() {
    return !isProductionAzure();
}
export function getConfig() {
    const s = getServerConfig();
    return {
        sql: {
            server: s.AZURE_SQL_SERVER,
            database: s.AZURE_SQL_DATABASE,
            user: s.AZURE_SQL_USER ?? '',
            password: s.AZURE_SQL_PASSWORD ?? '',
            ...transportSql(s),
        },
        auth: {
            // Les 3 champs AD/storage sont optionnels au niveau schema (backends sans
            // workforce AD/storage). getConfig() est appele par les apps qui en ont besoin
            // (elles les definissent) -> fallback '' sans casser le type (cf AZURE_SQL_USER).
            clientId: s.AZURE_AD_CLIENT_ID ?? '',
            tenantId: s.AZURE_AD_TENANT_ID ?? '',
            redirectUri: s.AZURE_AD_REDIRECT_URI ?? 'http://localhost:5173',
            scopes: ['User.Read', 'openid', 'profile', 'email'],
        },
        storage: {
            // ADR-057 : Managed Identity — `account` suffit (URL blob derivee). La
            // connection string Shared Key reste exposee mais DEPRECATED (non lue).
            account: s.AZURE_STORAGE_ACCOUNT ?? '',
            connectionString: s.AZURE_STORAGE_CONNECTION_STRING ?? '',
            containerName: s.AZURE_STORAGE_CONTAINER,
        },
        apiUrl: process.env.VITE_API_URL ?? 'http://localhost:3001',
        nodeEnv: s.NODE_ENV,
    };
}
/** Liste des 11 régions GS */
export const GS_REGIONS = [
    'Montréal',
    'Sainte-Catherine',
    'Saint-Jérôme',
    'Joliette',
    'Trois-Rivières',
    'Drummondville',
    'Sherbrooke',
    'Rouyn-Noranda',
    'Jonquière',
    'Gatineau',
    'Lévis',
];
/** Catégories d'articles GS */
export const GS_CATEGORIES = ['Signalisation', 'EPI', 'Lettrage'];
/**
 * Adresses publiques des applications du parc — SOURCE UNIQUE.
 *
 * POURQUOI CETTE CONSTANTE EXISTE (E-20260901-config-client-liens-en-dur,
 * E-20260901-hub-admin-deux-cibles). La même application avait DEUX adresses
 * selon l'endroit d'où on la cliquait : la tuile du catalogue ouvrait
 * `admin.groupesignalisation.ca`, l'item de barre latérale du lanceur ouvrait
 * l'URL Azure brute `gs-admin-....azurewebsites.net`. Deux origines = deux jeux
 * de cookies, deux historiques, deux favoris — pour une seule application. Et
 * la liste des adresses vivait à cinq endroits qui ne se connaissaient pas :
 * le catalogue du lanceur, sa barre latérale, son écran utilisateurs, le module
 * de renvoi de la Console UG, la carte de déménagement d'`admin`.
 *
 * Ce sont des ADRESSES STABLES, pas des variables d'environnement : même nature
 * que `GS_REGIONS` ci-dessus. Domaines personnalisés pour les applications
 * Azure ; adresse d'hébergement pour celles qui n'en ont pas encore. Une
 * adresse qui change se change dans le manifeste de son application, une
 * fois. Le gate `check:nav` (sens « adresse hors registre ») refuse
 * desormais tout litteral d'hote d'application ailleurs dans le code des apps.
 *
 * GENEREE DEPUIS LE REGISTRE DES APPLICATIONS (ADR-090, 2026-10-07) : une cle par
 * application qui declare une `adresse` dans son manifeste (`apps/<app>/deploiement.json`
 * ou `registre/hors-depot/<app>.json`) ; la cle est `noms.url` quand elle n est pas
 * celle de l application (`portail` pour api-portail, `ufWebEditor`). Un sous-domaine
 * nu devient `https://<adresse>.groupesignalisation.ca` ; une application hebergee
 * ailleurs (Firebase) declare son adresse entiere — le registre est la source unique
 * des ADRESSES du parc, pas seulement des domaines GS (mesure du 2026-09-15 : sans
 * elles, quatre tuiles « en service » etaient grisees alors que les quatre
 * repondaient). Naissance d'une application : son manifeste, puis
 * `npm run registre:generer` — plus d edition ici.
 */
export const GS_APP_URLS = {
    // registre:genere debut adresses
    // GENERE par npm run registre:generer depuis le registre des applications (ADR-090) — ne pas editer entre les marqueurs (adresses).
    /** GS-Admin — apps/admin/deploiement.json */
    admin: 'https://admin.groupesignalisation.ca',
    /** Documentation GS — apps/docs/deploiement.json */
    docs: 'https://docs.groupesignalisation.ca',
    /** GS-Formation — registre/hors-depot/formation.json */
    formation: 'https://gs-suiviformation.web.app',
    /** GS-Gestion — apps/gest/deploiement.json */
    gest: 'https://gest.groupesignalisation.ca',
    /** GS-Hub — apps/hub/deploiement.json */
    hub: 'https://hub.groupesignalisation.ca',
    /** GS-MCP — apps/mcp/deploiement.json */
    mcp: 'https://mcp.groupesignalisation.ca',
    /** GS-Ops — registre/hors-depot/ops.json */
    ops: 'https://appgroupesignalisation.ca',
    /** Portail — apps/api-portail/deploiement.json */
    portail: 'https://portail.groupesignalisation.ca',
    /** UF WebEditor — registre/hors-depot/uf-webeditor.json */
    ufWebEditor: 'https://uf-webeditor.web.app',
    /** Console UG — apps/ug/deploiement.json */
    ug: 'https://ug.groupesignalisation.ca',
    /** GS-ZoneSecur — apps/zonesecur/deploiement.json */
    zonesecur: 'https://zonesecur.groupesignalisation.ca',
    // registre:genere fin adresses
};
/**
 * UUID GS Quebec — tenant unique Phase 1 mono-tenant (HVB-01, ADR-036 amendement).
 * À retirer après Phase 2 (multi-tenant opérationnel).
 *
 * NE PAS UTILISER pour résoudre le tenant en runtime — utiliser `req.companyId`
 * depuis le middleware `injectCompanyId`. Cette constante sert UNIQUEMENT dans les
 * `readClaims` Phase 1 qui retournent `{ companyId: GS_PHASE1_COMPANY_ID }` pour les
 * apps sans claim Azure AD `companyId` (à venir Phase 2).
 */
export const GS_PHASE1_COMPANY_ID = '00000000-0000-0000-0000-000000000001';
/**
 * `company_id` de Goliax dans `ug.Companies` — GUID FIXE, ecrit une seule fois par la
 * migration `packages/db/sql/192_ug_company_goliax.sql` (DECISION-2026-09-25-USINE-GOLIAX-LUE-COMME-SOFI).
 * Aleatoire, hors de l'espace `...000x` : `...0002` sert de compagnie fictive aux tests.
 *
 * Meme usage borne que `GS_PHASE1_COMPANY_ID` : un chargement ou un seed qui doit
 * ECRIRE sous Goliax (lots bronze et couche servie de l'usine). Jamais pour resoudre le
 * tenant d'une requete — `req.companyId`, derive du JWT, reste la seule source.
 */
export const GOLIAX_COMPANY_ID = 'a8dfccf5-344d-477c-813a-ae28eeca579e';
/**
 * Rouge de marque GS pour les courriels HTML (#d51224 — hex des SVG
 * officiels, seede par la migration 114 dans ug.CompanyBranding).
 * Repli LITTERAL assume : un client de messagerie ne lit pas les variables
 * CSS. DETTE : la couleur devrait venir de ug.CompanyBranding par compagnie
 * (ADR-059 §2.5) — GEST n'a aujourd'hui aucun acces a ce schema.
 */
// ds-lint-exempt sur la ligne meme : scanBrandHex ne regarde QUE la ligne courante
// (scanApp, lui, remonte 2 lignes — les deux scanners different sur ce point).
export const GS_BRAND_RED_EMAIL = '#d51224'; // ds-lint-exempt: repli courriel HTML, un client de messagerie ne lit pas les variables CSS (ADR-059 §2.5, dette tracee ci-dessus)
/*
 * DEPLACE le 2026-08-12 — sentinelle « membre inconnu » (ADR-074 D3).
 *
 * `DIM_UNKNOWN_NATURAL_KEY`, `DIM_UNKNOWN_LABEL` et `DIM_UNKNOWN_IDS_PHASE1` vivaient
 * ici ET (pour la cle) dans `@gs/standards/dw-doctrine` sous le nom
 * `DW_UNKNOWN_MEMBER_KEY` : DEUX declarations de la meme doctrine, plus un litteral
 * `N'__UNK__'` en dur dans `employesTerrainMerge.ts`. Le label et les GUID n'avaient
 * AUCUN lecteur — une liste parallele de plus, deja divergente du seul endroit qui
 * les pose vraiment (mig 167).
 *
 * Source unique desormais : `packages/standards/src/dw-doctrine.ts`
 *   -> `DW_UNKNOWN_MEMBER_KEY`, `DW_UNKNOWN_MEMBER_LABEL`,
 *      `DW_UNKNOWN_MEMBER_IDS_PHASE1`, `sentinelMatchSql()`, `sentinelExclusionSql()`.
 *
 * Motif : la sentinelle est une DOCTRINE dimensionnelle (ADR-074), pas une variable
 * d'environnement. `@gs/config` valide l'env ; `@gs/standards` porte les standards
 * executables que les gates ET les transforms consomment. La direction de dependance
 * impose ce sens : `@gs/standards` a deja `@gs/config` en devDependency (tests de
 * parite), donc l'inverse (`config -> standards`) serait un cycle de workspace.
 */
//# sourceMappingURL=index.js.map